(function (window, document) {
	'use strict';

	function ResizeObserverManager() {
		this._observer = null;
		this._targets = new Map();
		this._queue = new Set();
		this._rafId = null;
		this._supported = typeof window.ResizeObserver === 'function';
	}

	ResizeObserverManager.prototype.observe = function (target, callback) {
		if (!this._supported || !target || typeof callback !== 'function') {
			return;
		}
		if (!this._observer) {
			this._observer = new ResizeObserver(this._handleEntries.bind(this));
		}
		this._targets.set(target, callback);
		this._observer.observe(target);
	};

	ResizeObserverManager.prototype.unobserve = function (target) {
		if (!this._observer || !target) {
			return;
		}
		this._observer.unobserve(target);
		this._targets.delete(target);
	};

	ResizeObserverManager.prototype._handleEntries = function (entries) {
		for (var i = 0; i < entries.length; i++) {
			this._queue.add(entries[i].target);
		}
		this._scheduleFlush();
	};

	ResizeObserverManager.prototype._scheduleFlush = function () {
		if (this._rafId) {
			return;
		}
		this._rafId = requestAnimationFrame(this._flush.bind(this));
	};

	ResizeObserverManager.prototype._flush = function () {
		this._rafId = null;
		var targets = Array.from(this._queue);
		this._queue.clear();
		for (var i = 0; i < targets.length; i++) {
			var target = targets[i];
			var callback = this._targets.get(target);
			if (callback) {
				callback(target);
			}
		}
	};

	function MutationObserverManager() {
		this._entries = new WeakMap();
		this._supported = typeof window.MutationObserver === 'function';
	}

	MutationObserverManager.prototype.observe = function (root, callback, options) {
		if (!this._supported || !root || typeof callback !== 'function') {
			return;
		}
		var entry = this._entries.get(root);
		if (!entry) {
			entry = {
				observer: null,
				callbacks: new Set(),
				options: null,
				pending: [],
				rafId: null
			};
			this._entries.set(root, entry);
		}

		entry.callbacks.add(callback);
		var nextOptions = options || { childList: true, subtree: true };
		var merged = mergeMutationOptions(entry.options, nextOptions);
		if (!entry.observer) {
			entry.observer = new MutationObserver(this._handleMutations.bind(this, root));
			entry.options = merged;
			entry.observer.observe(root, merged);
			return;
		}

		if (!sameMutationOptions(entry.options, merged)) {
			entry.options = merged;
			entry.observer.disconnect();
			entry.observer.observe(root, merged);
		}
	};

	MutationObserverManager.prototype.unobserve = function (root, callback) {
		var entry = this._entries.get(root);
		if (!entry) {
			return;
		}
		if (callback) {
			entry.callbacks.delete(callback);
		} else {
			entry.callbacks.clear();
		}
		if (entry.callbacks.size === 0) {
			if (entry.observer) {
				entry.observer.disconnect();
			}
			this._entries.delete(root);
		}
	};

	MutationObserverManager.prototype._handleMutations = function (root, mutations) {
		var entry = this._entries.get(root);
		if (!entry) {
			return;
		}
		entry.pending = entry.pending.concat(mutations);
		if (entry.rafId) {
			return;
		}
		entry.rafId = requestAnimationFrame(this._flush.bind(this, root));
	};

	MutationObserverManager.prototype._flush = function (root) {
		var entry = this._entries.get(root);
		if (!entry) {
			return;
		}
		entry.rafId = null;
		var mutations = entry.pending.slice();
		entry.pending = [];
		entry.callbacks.forEach(function (callback) {
			callback(mutations);
		});
	};

	function mergeMutationOptions(base, incoming) {
		if (!base) {
			return incoming;
		}
		var merged = {
			childList: Boolean(base.childList || incoming.childList),
			subtree: Boolean(base.subtree || incoming.subtree)
		};
		if (base.attributes || incoming.attributes) {
			merged.attributes = true;
			merged.attributeFilter = base.attributeFilter || incoming.attributeFilter;
		}
		if (base.characterData || incoming.characterData) {
			merged.characterData = true;
		}
		return merged;
	}

	function sameMutationOptions(a, b) {
		if (!a || !b) {
			return false;
		}
		return Boolean(a.childList) === Boolean(b.childList)
			&& Boolean(a.subtree) === Boolean(b.subtree)
			&& Boolean(a.attributes) === Boolean(b.attributes)
			&& Boolean(a.characterData) === Boolean(b.characterData);
	}

	window.ReviewFlowObserverManager = {
		resize: new ResizeObserverManager(),
		mutation: new MutationObserverManager()
	};
})(window, document);
